import React, { useContext, useState, useEffect } from 'react';
import './ItemShop.css';
import '../../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/StatsDisplay/StatsDisplay.css';
import GameAssetsContext from '../../../XXX/context/GameAssetContext';
import { ItemDetails } from '../../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/SettingsPanel/ItemShopPanel2';
import RecipeTree from './RecipeTree';
import DataService from '../../../XXX/DataService';

// Extend ItemDetails to include categories and itemCategory
interface ExtendedItemDetails extends ItemDetails {
  categories?: string[];
  itemCategory?: string[]; // Add this field to match our JSON structure
  itemGroups?: string[]; // Item group restrictions (e.g. "Fatality", "Lifeline")
  removed?: boolean; // Flag for items that have been removed from the game
}



// Role type for quest system
type Role = 'top' | 'jgl' | 'mid' | 'bot' | 'sup';

interface ItemShopProps {
  isOpen: boolean;
  championName: string;
  onClose: () => void;
  onItemSelect?: (itemId: string, itemName: string) => void;
  className?: string;
  // Build order management
  currentBuildOrder?: {
    starter?: string; // Starter item ID
    mainItems: string[]; // Array of item IDs in purchase order (6 normally, 7 for Bot quest with boots at any position)
  };
  onBuildOrderChange?: (championName: string, buildOrder: { starter?: string; mainItems: string[] }) => void;
  // Role quest props
  role?: Role;
  onRoleChange?: (role: Role) => void;
}

// Available categories matching the in-game store
const CATEGORIES = [
  'All Items',
  'Fighter',
  'Marksman',
  'Assassin',
  'Mage',
  'Tank',
  'Support'
];

// Map stat keys to their icon class names
const STAT_ICON_MAP: Record<string, string> = {
  'attackDamage': 'attack-damage-icon',
  'abilityPower': 'ability-power-icon',
  'armor': 'armor-icon',
  'magicResistance': 'magic-resist-icon',
  'attackSpeed': 'attack-speed-icon',
  'abilityHaste': 'ability-haste-icon',
  'health': 'health-icon',
  'mana': 'resource-icon',
  'movementSpeed': 'movement-speed-icon',
  'criticalStrikeChance': 'crit-chance-icon',
  'criticalStrikeDamage': 'crit-damage-icon',
  'critDamage': 'crit-chance-icon',
  'lifeSteal': 'life-steal-icon',
  'omnivamp': 'omnivamp-icon',
  'armorPenetrationPercent': 'armor-penetration-icon',
  'lethality': 'armor-penetration-icon',
  'magicPenetration': 'magic-penetration-icon',
  'magicPenetrationPercent': 'magic-penetration-icon',
  'tenacity': 'tenacity-icon',
  'baseHealthRegenPercent': 'health-regen-icon',
  'baseManaRegenPercent': 'resource-regen-icon',
  'baseHealthRegen': 'health-regen-icon',
  'baseManaRegen': 'resource-regen-icon',
  'healAndShieldPower': 'heal-shield-power-icon',
  'gold': 'gold-icon',
};

// Function to get a display name for a stat key
const getStatDisplayName = (statKey: string): string => {
  const displayNameMap: Record<string, string> = {
    'attackDamage': 'Attack Damage',
    'abilityPower': 'Ability Power',
    'armor': 'Armor',
    'magicResistance': 'Magic Resist',
    'attackSpeed': 'Attack Speed',
    'abilityHaste': 'Ability Haste',
    'health': 'Health',
    'mana': 'Mana',
    'movementSpeed': 'Movement Speed',
    'criticalStrikeChance': 'Critical Strike',
    'critDamage': 'Critical Damage',
    'lifeSteal': 'Life Steal',
    'omnivamp': 'Omnivamp',
    'armorPenetrationPercent': 'Armor Penetration (%)',
    'lethality': 'Lethality',
    'magicPenetration': 'Magic Penetration',
    'magicPenetrationPercent': 'Magic Penetration (%)',
    'tenacity': 'Tenacity',
    'healthRegen': 'Health Regen',
    'manaRegen': 'Mana Regen'
  };
  
  return displayNameMap[statKey] || statKey;
};

const ItemShop: React.FC<ItemShopProps> = ({
  isOpen,
  championName,
  onClose,
  onItemSelect,
  className = '',
  currentBuildOrder = { starter: undefined, mainItems: [] },
  onBuildOrderChange,
  role = 'mid',
  onRoleChange
}) => {
  const { gameWideAssets } = useContext(GameAssetsContext);
  const [activeCategory, setActiveCategory] = useState<string>('All Items');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [hoveredItem, setHoveredItem] = useState<ExtendedItemDetails | null>(null);

  /** Normalize for fuzzy search: strip apostrophes, periods, spaces, hyphens and lowercase */
  const normalizeForSearch = (str: string): string => {
    return str.toLowerCase().replace(/['\.\s-]/g, '');
  };
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [popularItems, setPopularItems] = useState<ExtendedItemDetails[]>([]);
  const [localBuildOrder, setLocalBuildOrder] = useState(currentBuildOrder);
  const [apiVersion, setApiVersion] = useState<string>('15.9.1');
  
  // Local role state - only propagates to parent on save
  const [localRole, setLocalRole] = useState<Role>(role);
  
  // Drag-and-drop state for reordering main items
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  
  // Check if Bot quest is active (7th boots slot) - use LOCAL role
  const hasBotQuestSlot = localRole === 'bot';
  
  // Helper to check if item is boots
  const isBootsItem = (itemId: string | number): boolean => {
    const id = typeof itemId === 'string' ? parseInt(itemId) : itemId;
    const bootsIds = [
      1001, // Basic Boots
      3006, 3009, 3020, 3047, 3111, 3117, 3158, // T1 boots
      223009, 223020, 223047, 223111, 223158, 223006 // T2 boots
    ];
    return bootsIds.includes(id);
  };

  // Helper to get item groups for a given item ID
  const getItemGroups = (itemId: string): string[] => {
    const item = gameWideAssets?.itemsDetails?.find(i => i.id === itemId || parseInt(i.id) === parseInt(itemId));
    return (item as ExtendedItemDetails)?.itemGroups || [];
  };

  // Helper to collect all item groups currently in the build
  const getOwnedGroups = (): Set<string> => {
    const groups = new Set<string>();
    const allItems = getAllBuildItems();
    for (const id of allItems) {
      for (const group of getItemGroups(id)) {
        groups.add(group);
      }
    }
    return groups;
  };

  // Check if an item conflicts with any item already in the build (same item group)
  const getGroupConflict = (itemId: string): string | null => {
    const itemGroupsList = getItemGroups(itemId);
    if (itemGroupsList.length === 0) return null;
    const ownedGroups = getOwnedGroups();
    for (const group of itemGroupsList) {
      if (ownedGroups.has(group)) return group;
    }
    return null;
  };
  
  // Fetch latest API version
  useEffect(() => {
    DataService.fetchLatestVersion().then(setApiVersion);
  }, []);

  
  // Sync local build order with props
  useEffect(() => {
    setLocalBuildOrder(currentBuildOrder);
  }, [currentBuildOrder]);
  
  // Sync local role with props (when shop opens or role prop changes from parent)
  useEffect(() => {
    setLocalRole(role);
  }, [role]);

  // Track previous role to detect changes
  const [prevHasBotQuestSlot, setPrevHasBotQuestSlot] = useState(hasBotQuestSlot);
  
  // Get max items allowed (7 for Bot quest, 6 for others)
  const maxMainItems = hasBotQuestSlot ? 7 : 6;
  
  // Auto-reorganize build when Bot quest status changes
  useEffect(() => {
    // Switching TO Bot quest - add a 7th item from popular items
    if (hasBotQuestSlot && !prevHasBotQuestSlot) {
      // If we have 6 items, add the next popular item as the 7th
      if (localBuildOrder.mainItems.length === 6 && popularItems.length > 0) {
        const currentItemIds = new Set(localBuildOrder.mainItems);
        if (localBuildOrder.starter) {
          currentItemIds.add(localBuildOrder.starter);
        }
        
        // Find the next popular item that's not already in the build
        const nextPopularItem = popularItems.find(item => !currentItemIds.has(item.id));
        if (nextPopularItem) {
          setLocalBuildOrder(prev => ({
            ...prev,
            mainItems: [...prev.mainItems, nextPopularItem.id]
          }));
        }
      }
    }
    
    // Switching AWAY from Bot quest - trim to 6 items if we have 7
    if (!hasBotQuestSlot && prevHasBotQuestSlot) {
      if (localBuildOrder.mainItems.length > 6) {
        // Remove the 7th item
        setLocalBuildOrder(prev => ({
          ...prev,
          mainItems: prev.mainItems.slice(0, 6)
        }));
      }
    }
    
    setPrevHasBotQuestSlot(hasBotQuestSlot);
  }, [hasBotQuestSlot, prevHasBotQuestSlot, localBuildOrder.mainItems, localBuildOrder.starter, popularItems]);
  
  // Build order management functions
  const addItemToBuild = (itemId: string, isStarter: boolean = false) => {
    if (isStarter) {
      setLocalBuildOrder(prev => ({
        ...prev,
        starter: itemId
      }));
    } else {
      const allMainItems = [...localBuildOrder.mainItems];
      
      // Only enforce "1 of each" restriction for legendary items
      // Epic, basic, and starter items can be added multiple times
      const itemDetail = gameWideAssets?.itemsDetails?.find(i => i.id === itemId || parseInt(i.id) === parseInt(itemId));
      const isLegendary = itemDetail?.tier === 'legendary';
      if (isLegendary && allMainItems.includes(itemId)) {
        return; // Already have this legendary item
      }

      // Check item group conflict (e.g. can't have Lord Dominik's + Mortal Reminder)
      const conflict = getGroupConflict(itemId);
      if (conflict) {
        console.log(`[ItemShop] ⛔ Cannot add item ${itemId}: conflicts with group "${conflict}"`);
        return;
      }
      
      // Enforce boots limit: only 1 pair of boots allowed
      if (isBootsItem(itemId)) {
        const existingBootsIndex = allMainItems.findIndex(id => isBootsItem(id));
        if (existingBootsIndex >= 0) {
          // Replace existing boots with new boots
          allMainItems[existingBootsIndex] = itemId;
          setLocalBuildOrder(prev => ({
            ...prev,
            mainItems: allMainItems
          }));
          return;
        }
      }
      
      // Add item if there's room
      if (allMainItems.length < maxMainItems) {
        allMainItems.push(itemId);
        setLocalBuildOrder(prev => ({
          ...prev,
          mainItems: allMainItems
        }));
      }
    }
  };
  
  const removeItemFromBuild = (itemId: string, indexToRemove?: number) => {
    setLocalBuildOrder(prev => {
      const newBuildOrder = { ...prev };
      if (newBuildOrder.starter === itemId && indexToRemove === undefined) {
        newBuildOrder.starter = undefined;
      } else if (indexToRemove !== undefined) {
        // Remove by specific index (for build order bar where duplicates are possible)
        newBuildOrder.mainItems = [...newBuildOrder.mainItems];
        newBuildOrder.mainItems.splice(indexToRemove, 1);
      } else {
        // Remove first occurrence only
        const idx = newBuildOrder.mainItems.indexOf(itemId);
        if (idx >= 0) {
          newBuildOrder.mainItems = [...newBuildOrder.mainItems];
          newBuildOrder.mainItems.splice(idx, 1);
        }
      }
      return newBuildOrder;
    });
  };

  // Drag-and-drop handlers for reordering main items
  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragEnd = () => {
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      setLocalBuildOrder(prev => {
        const newItems = [...prev.mainItems];
        const [dragged] = newItems.splice(dragIndex, 1);
        newItems.splice(dragOverIndex, 0, dragged);
        return { ...prev, mainItems: newItems };
      });
    }
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };
  
  const saveBuildOrder = () => {
    // Save the build order
    if (onBuildOrderChange) {
      onBuildOrderChange(championName, localBuildOrder);
    }
    // Save the role change
    if (onRoleChange && localRole !== role) {
      onRoleChange(localRole);
    }
  };
  
  const handleClose = () => {
    saveBuildOrder(); // Auto-save on close
    onClose();
  };
  
  // Get all items in build (starter + main items)
  const getAllBuildItems = (): string[] => {
    const allItems = [];
    if (localBuildOrder.starter) {
      allItems.push(localBuildOrder.starter);
    }
    allItems.push(...localBuildOrder.mainItems);
    return allItems;
  };
  
  // Get build item details
  const getBuildItemDetails = (itemIds: string[]): ExtendedItemDetails[] => {
    return itemIds
      .map(itemId => {
        const itemDetails = gameWideAssets?.itemsDetails?.find(item => 
          item.id === itemId || parseInt(item.id) === parseInt(itemId)
        );
        if (itemDetails) {
          const extendedItem = itemDetails as ExtendedItemDetails;
          let categories = extendedItem.itemCategory || ['All Items'];
          if (!categories.includes('All Items')) {
            categories = [...categories, 'All Items'];
          }
          return {
            ...extendedItem,
            categories: categories
          } as ExtendedItemDetails;
        }
        return null;
      })
      .filter((item): item is ExtendedItemDetails => item !== null);
  };
  
  // Fetch popular items for the champion
  useEffect(() => {
    const fetchPopularItems = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/stats/champion/${championName}/item-build`);
        if (response.ok) {
          const buildData = await response.json();
          
          // Extract popular item IDs from build data
          const popularItemIds: number[] = [];
          
          if (buildData.starter) {
            popularItemIds.push(buildData.starter.item_id);
          }
          
          if (buildData.coreItems) {
            popularItemIds.push(buildData.coreItems.item1_id);
            popularItemIds.push(buildData.coreItems.item2_id);
            popularItemIds.push(buildData.coreItems.item3_id);
          }
          
          if (buildData.supplementaryItems) {
            // Add top 6 supplementary items
            buildData.supplementaryItems.slice(0, 6).forEach((item: any) => {
              popularItemIds.push(item.item_id);
            });
          }
          
          // Get item details for popular items
          const popularItemDetails: ExtendedItemDetails[] = popularItemIds
            .map(itemId => {
              const itemDetails = gameWideAssets?.itemsDetails?.find(item => 
                item.id === itemId.toString() || parseInt(item.id) === itemId
              );
              if (itemDetails) {
                const extendedItem = itemDetails as ExtendedItemDetails;
                let categories = extendedItem.itemCategory || ['All Items'];
                if (!categories.includes('All Items')) {
                  categories = [...categories, 'All Items'];
                }
                return {
                  ...extendedItem,
                  categories: categories
                } as ExtendedItemDetails;
              }
              return null;
            })
            .filter((item): item is ExtendedItemDetails => item !== null);
            
          setPopularItems(popularItemDetails);
        }
      } catch (error) {
        console.error('Error fetching popular items:', error);
      }
    };
    
    if (isOpen && championName && gameWideAssets?.itemsDetails) {
      fetchPopularItems();
    }
  }, [isOpen, championName, gameWideAssets?.itemsDetails]);
  
  // Get all items and apply categories from the JSON data
  // Filter out removed items first
  const items: ExtendedItemDetails[] = (gameWideAssets?.itemsDetails || [])
    .filter(item => !(item as ExtendedItemDetails).removed)
    .map(item => {
      // Use the itemCategory field from the JSON file or default to ['All Items']
      const extendedItem = item as ExtendedItemDetails;
      let categories = extendedItem.itemCategory || ['All Items'];
      
      // Always include 'All Items' category
      if (!categories.includes('All Items')) {
        categories = [...categories, 'All Items'];
      }
      
      return {
        ...extendedItem,
        categories: categories
      };
    });
  
  // Filter items by active category and search term (with normalized fuzzy matching)
  const normalizedSearch = normalizeForSearch(searchTerm);
  const filteredItems = items.filter(item => {
    const matchesCategory = item.categories && item.categories.includes(activeCategory);
    const matchesSearch = normalizedSearch === '' || 
      normalizeForSearch(item.name).includes(normalizedSearch);
    return matchesCategory && matchesSearch;
  });
  
  // Group items by tier for the selected category
  const itemsByTier = {
    legendary: filteredItems.filter(item => item.tier === 'legendary'),
    epic: filteredItems.filter(item => item.tier === 'epic'),
    basic: filteredItems.filter(item => item.tier === 'basic'),
    starter: filteredItems.filter(item => item.tier === 'starter'),
    boots: filteredItems.filter(item => item.tier === 'boots'),
  };
  
  // Sort items within each tier: when searching, prioritize "starts with" then alphabetical;
  // otherwise sort by cost descending
  Object.keys(itemsByTier).forEach((tier) => {
    if (normalizedSearch !== '') {
      itemsByTier[tier as keyof typeof itemsByTier].sort((a, b) => {
        const normA = normalizeForSearch(a.name);
        const normB = normalizeForSearch(b.name);
        const aStartsWith = normA.startsWith(normalizedSearch);
        const bStartsWith = normB.startsWith(normalizedSearch);
        // 1. Prioritize names that start with the query
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        // 2. Alphabetical within same group
        return a.name.localeCompare(b.name);
      });
    } else {
      itemsByTier[tier as keyof typeof itemsByTier].sort((a, b) => b.cost - a.cost);
    }
  });
  
  const handleItemSelect = (itemId: string, itemName: string) => {
    if (onItemSelect) {
      onItemSelect(itemId, itemName);
    }
  };
  
  const handleItemMouseEnter = (item: ExtendedItemDetails, event: React.MouseEvent) => {
    setMousePosition({ x: event.clientX, y: event.clientY });
    setHoveredItem(item);
  };
  
  const handleItemMouseMove = (event: React.MouseEvent) => {
    setMousePosition({ x: event.clientX, y: event.clientY });
  };
  
  const handleItemMouseLeave = () => {
    setHoveredItem(null);
  };

  // Helper function to get item details for the details panel
  const getItemDetails = (itemId: number): ItemDetails | null => {
    if (!gameWideAssets?.itemsDetails) return null;
    return gameWideAssets.itemsDetails.find(item => 
      item.id === itemId.toString() || parseInt(item.id) === itemId
    ) || null;
  };

  // Helper function to get item image URL
  const getItemImageUrl = (itemId: number) => {
    return `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/${itemId}.png`;
  };

  // Function to get a display name for a stat key
  const getStatDisplayName = (statKey: string): string => {
    const displayNameMap: Record<string, string> = {
      'attackDamage': 'Attack Damage',
      'abilityPower': 'Ability Power',
      'armor': 'Armor',
      'magicResistance': 'Magic Resist',
      'attackSpeed': 'Attack Speed',
      'abilityHaste': 'Ability Haste',
      'health': 'Health',
      'mana': 'Mana',
      'movementSpeed': 'Movement Speed',
      'criticalStrikeChance': 'Critical Strike',
      'critDamage': 'Critical Damage',
      'lifeSteal': 'Life Steal',
      'omnivamp': 'Omnivamp',
      'armorPenetrationPercent': 'Armor Penetration (%)',
      'lethality': 'Lethality',
      'magicPenetration': 'Magic Penetration',
      'magicPenetrationPercent': 'Magic Penetration (%)',
      'tenacity': 'Tenacity',
      'healthRegen': 'Health Regen',
      'manaRegen': 'Mana Regen'
    };
    
    return displayNameMap[statKey] || statKey;
  };
  
  const itemImageBaseUrl = `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/`;
  
  // Role to icon key mapping
  const ROLE_TO_ICON_KEY: Record<Role, string> = {
    top: 'TOP',
    jgl: 'JUNGLE',
    mid: 'MIDDLE',
    bot: 'BOTTOM',
    sup: 'UTILITY'
  };
  const ROLE_ORDER: Role[] = ['top', 'jgl', 'mid', 'bot', 'sup'];

  // Special render function for build order (starter + 6-7 main items)
  const renderBuildOrder = () => {
    const starterItem = localBuildOrder.starter ? getBuildItemDetails([localBuildOrder.starter])[0] : null;
    const mainItems = getBuildItemDetails(localBuildOrder.mainItems);
    const allBuildItems = getAllBuildItems();
    
    return (
      <div className="itemshop-build-order-section">
        <div className="itemshop-title-row">
          <h5 className="itemshop-section-title">Build Order</h5>
          <button className="itemshop-close-button" onClick={handleClose}>×</button>
        </div>
        <div className="itemshop-build-description">
          <p>Adjust the build that will be set as the default for the wave progression table below.</p>
          <p>This can still be adjusted for individual waves later - it's not final, but sets the baseline progression.</p>
        </div>
        
        {/* Role Quest Selector */}
        {onRoleChange && (
          <div className="itemshop-role-selector">
            <span className="itemshop-role-label">Role & Quest:</span>
            <div className="itemshop-role-buttons">
              {ROLE_ORDER.map(r => (
                <button
                  key={r}
                  className={`itemshop-role-btn ${localRole === r ? 'active' : ''}`}
                  onClick={() => setLocalRole(r)}
                  title={r.charAt(0).toUpperCase() + r.slice(1)}
                >
                  <img
                    src={gameWideAssets.roleIcons[ROLE_TO_ICON_KEY[r]]}
                    alt={r}
                    style={{
                      width: 18,
                      height: 18,
                      filter: localRole === r ? 'none' : 'grayscale(1) opacity(0.4)',
                      verticalAlign: 'middle'
                    }}
                  />
                </button>
              ))}
            </div>
            {localRole === 'jgl' && (
              <span className="itemshop-role-note">Jungle income currently uses mid lane gold. Jungle-specific gold coming soon.</span>
            )}
          </div>
        )}
        
        <div className="itemshop-build-container">
          {/* Headers Row */}
          <div className="itemshop-headers-row">
            <div className="itemshop-starter-header">
              <div className="itemshop-slot-label">STARTER</div>
            </div>
            <div className="itemshop-divider"></div>
            <div className="itemshop-main-header">
              <div className="itemshop-slot-label">MAIN BUILD ({hasBotQuestSlot ? '7 ITEMS' : '6 ITEMS'})</div>
              <div className="itemshop-build-actions">
                <button className="itemshop-save-button" onClick={saveBuildOrder}>
                  Save Build Order
                </button>
              </div>
            </div>
          </div>
          
          {/* Items Row */}
          <div className="itemshop-items-row">
            <div className="itemshop-starter-items">
              <div className="itemshop-starter-slot">
                {starterItem ? (
                  <div
                    className="itemshop-item-box itemshop-starter-item"
                    onDoubleClick={() => removeItemFromBuild(starterItem.id)}
                    onMouseEnter={(e) => handleItemMouseEnter(starterItem, e)}
                    onMouseMove={handleItemMouseMove}
                    onMouseLeave={handleItemMouseLeave}
                    title="Double-click to remove starter item"
                  >
                    <img 
                      src={`${itemImageBaseUrl}${starterItem.image?.full || 'missing.png'}`}
                      alt={starterItem.name}
                      onError={(e) => {
                        e.currentTarget.src = `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/7050.png`;
                      }}
                    />
                    <div className="itemshop-remove-indicator">×</div>
                  </div>
                ) : (
                  <div className="itemshop-item-box itemshop-empty-slot">
                    <div className="itemshop-empty-indicator">+</div>
                  </div>
                )}
              </div>
            </div>
            <div className="itemshop-divider"></div>
            <div className="itemshop-main-items">
              <div className="itemshop-main-slots">
                {/* Render existing main items (up to 7 for Bot quest, 6 for others) */}
                {mainItems.map((item, index) => (
                  <div
                    key={`${item.id}-${index}`}
                    className={`itemshop-item-box itemshop-main-item${dragIndex === index ? ' dragging' : ''}${dragOverIndex === index ? ' drag-over' : ''}`}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDragEnd={handleDragEnd}
                    onDoubleClick={() => removeItemFromBuild(item.id, index)}
                    onMouseEnter={(e) => handleItemMouseEnter(item, e)}
                    onMouseMove={handleItemMouseMove}
                    onMouseLeave={handleItemMouseLeave}
                    title="Drag to reorder · Double-click to remove"
                  >
                    <img 
                      src={`${itemImageBaseUrl}${item.image?.full || 'missing.png'}`}
                      alt={item.name}
                      onError={(e) => {
                        e.currentTarget.src = `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/7050.png`;
                      }}
                    />
                    <div className="itemshop-remove-indicator">×</div>
                  </div>
                ))}
                
                {/* Render empty slots for remaining positions */}
                {Array.from({ length: maxMainItems - mainItems.length }).map((_, index) => (
                  <div key={`empty-main-${index}`} className="itemshop-item-box itemshop-empty-slot">
                    <div className="itemshop-empty-indicator">+</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderItems = (items: ExtendedItemDetails[], sectionTitle?: string, isInventory: boolean = false) => {
    return (
      <div className="grid-item-section">
        {sectionTitle && <h5 className="section-title subtle-header">{sectionTitle}</h5>}
        <div className="grid-item-row">
          {items.map((item) => {
            const imageUrl = `${itemImageBaseUrl}${item.image?.full || 'missing.png'}`;
            const allBuildItems = getAllBuildItems();
            // Only mark as "owned" (blocking duplicates) for legendary items
            const isLegendaryItem = item.tier === 'legendary';
            const isOwned = isLegendaryItem && allBuildItems.includes(item.id);
            const isBuildFull = localBuildOrder.mainItems.length >= maxMainItems;
            const itemDetails = gameWideAssets?.itemsDetails?.find(i => i.id === item.id);
            const isStarterItem = itemDetails?.tier === 'starter' || (itemDetails?.cost && itemDetails.cost < 500);
            const itemIsBoots = isBootsItem(item.id);
            // Check if build already has boots (only 1 pair allowed)
            const hasBootsInBuild = localBuildOrder.mainItems.some(id => isBootsItem(id));
            // Check item group conflict
            const groupConflict = !isOwned ? getGroupConflict(item.id) : null;
            const isGroupBlocked = !!groupConflict;
            const canAddItem = !isGroupBlocked && (!isBuildFull || (itemIsBoots && hasBootsInBuild)); // Can replace boots even if full
            
            return (
              <div
                key={item.id}
                className={`grid-item-box ${isOwned && !isInventory ? 'owned' : ''} ${isInventory ? 'inventory-item' : ''} ${isGroupBlocked ? 'group-blocked' : ''}`}
                onClick={() => {
                  if (!isInventory) {
                    handleItemSelect(item.id, item.name);
                  }
                }}
                onDoubleClick={() => {
                  if (isInventory) {
                    removeItemFromBuild(item.id);
                  } else if (!isOwned && !isGroupBlocked) {
                    if (isStarterItem) {
                      addItemToBuild(item.id, true);
                    } else if (canAddItem) {
                      addItemToBuild(item.id, false);
                    }
                  } else if (isOwned) {
                    removeItemFromBuild(item.id);
                  }
                }}
                onMouseEnter={(e) => handleItemMouseEnter(item, e)}
                onMouseMove={handleItemMouseMove}
                onMouseLeave={handleItemMouseLeave}
                title={
                  isInventory 
                    ? "Double-click to remove" 
                    : isGroupBlocked
                      ? `Blocked: already own a "${groupConflict}" item`
                      : isOwned 
                        ? "Double-click to remove" 
                        : isStarterItem
                          ? "Double-click to set as starter"
                          : canAddItem
                            ? "Double-click to add to build"
                            : "Build full"
                }
              >
                <img 
                  src={imageUrl} 
                  alt={item.name}
                  onError={(e) => {
                    e.currentTarget.src = `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/7050.png`;
                  }}
                />
                {!isInventory && <div className="item-price">{item.cost}</div>}
                {isInventory && (
                  <div className="remove-indicator">×</div>
                )}
                {isGroupBlocked && (
                  <div className="group-blocked-overlay">✕</div>
                )}
              </div>
            );
          })}
          
          {/* Empty slots for inventory */}
          {isInventory && Array.from({ length: 6 - items.length }).map((_, index) => (
            <div key={`empty-${index}`} className="item-box empty-slot">
              <div className="empty-slot-indicator">+</div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  


  if (!isOpen) {
    return null;
  }
  
  return (
    <div className={`grid-item-shop-container ${className}`}>
      {/* Full-width item shop */}
      <div className="grid-item-shop-content">
        {/* Build Order Section */}
        {renderBuildOrder()}
        
        {/* Popular Items Section */}
        {popularItems.length > 0 && (
          <div className="popular-items-section">
            {renderItems(popularItems, "Popular Items")}
          </div>
        )}
        
        {/* Fixed Category Header */}
        <div className="grid-category-search-container">
          <div className="grid-item-category-tabs">
            {CATEGORIES.map(category => (
              <button
                key={category}
                className={`grid-category-tab ${activeCategory === category ? 'active' : ''}`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
          <input
            type="text"
            className="grid-item-search-input"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        {/* Scrollable Items Content */}
        <div className="grid-items-scrollable-content">
          <div className="items-by-tier">
          {Object.entries(itemsByTier).map(([tier, tierItems]) => (
            tierItems.length > 0 && (
              <div key={tier} className="tier-section">
                <h5 className="subtle-header">{tier.charAt(0).toUpperCase() + tier.slice(1)}</h5>
                {renderItems(tierItems)}
              </div>
            )
          ))}
          </div>
        </div>
      </div>
      
      {/* Tooltip */}
      {hoveredItem && (
        <div 
          className="item-tooltip"
          style={{
            position: 'fixed',
            top: `${mousePosition.y + 10}px`,
            left: `${mousePosition.x + 10}px`,
            zIndex: 10000,
          }}
        >
          <div className="tooltip-content">
            <div className="item-header">
              <img 
                src={`${itemImageBaseUrl}${hoveredItem?.image?.full || 'missing.png'}`} 
                alt={hoveredItem?.name || 'Item'}
                className="tooltip-item-image"
              />
              <div className="tooltip-item-info">
                <div className="tooltip-item-name">{hoveredItem?.name}</div>
                <div className="tooltip-item-cost">
                  <div className="stat-icon gold-icon"></div>
                  {hoveredItem?.cost}
                </div>
              </div>
            </div>
            
            {/* Two-column layout for details */}
            <div className="tooltip-details-container">
              {/* Left side - Stats and Passives */}
              <div className="tooltip-left-column">
                {/* Stats section */}
                {hoveredItem?.stats && Object.keys(hoveredItem.stats).length > 0 && (
                  <div className="tooltip-stats">
                    <h6>Stats</h6>
                    <div className="tooltip-stats-list">
                      {Object.entries(hoveredItem.stats).map(([key, value]) => (
                        <div key={key} className="tooltip-stat-row">
                          <div className={`stat-icon ${STAT_ICON_MAP[key] || 'default-stat-icon'}`}></div>
                          <div className="tooltip-stat-value">{value}</div>
                          <div className="tooltip-stat-name">{getStatDisplayName(key)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Passives section */}
                {hoveredItem?.passives && hoveredItem.passives.length > 0 && (
                  <div className="tooltip-passives">
                    <h6>Passives</h6>
                    <div className="tooltip-passives-list">
                      {hoveredItem.passives.map((passive, index) => (
                        <div key={index} className="tooltip-passive">
                          <div className="tooltip-passive-name">{passive.name}</div>
                          <div className="tooltip-passive-description">{passive.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Right side - Recipe Tree */}
              <div className="tooltip-right-column">
                <div className="tooltip-recipe-section">
                  <h6>Recipe</h6>
                  <div className="tooltip-recipe-tree">
                    <RecipeTree 
                      itemId={parseInt(hoveredItem?.id || '0')}
                      apiVersion={apiVersion}
                      className="tooltip-recipe-tree-component"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemShop;
